import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  manifestsAPI,
  consigneesAPI,
  freightAPI,
  manifestTemplatesAPI,
  routingAPI,
} from '../../services/api';
import { buildHammerManifestUrl } from '../../utils/hammerUrls';
import { buildManifestDataFromFreight } from '../../utils/manifest/buildManifestDataFromFreight';
import Table from '../Common/Table';
import Modal from '../Common/Modal';
import ManifestPrintPreview from './ManifestPrintPreview';
import '../../styles/HammerLinks.css';

const emptyForm = () => ({
  freight_id: '',
  manifest_template_id: '',
  manifest_number: '',
  ship_date: '',
  service_level: 'Standard',
  stop_number: '',
  pieces: '',
  shipped_value: '',
  consignee_id: '',
  delivery_address: '',
  delivery_city: '',
  delivery_state: '',
  delivery_zip_code: '',
  delivery_window: '',
  appointment_time: '',
});

const ManifestManagement = () => {
  const [manifests, setManifests] = useState([]);
  const [consignees, setConsignees] = useState([]);
  const [freightList, setFreightList] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [defaultDepot, setDefaultDepot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingManifest, setEditingManifest] = useState(null);
  const [formData, setFormData] = useState(emptyForm());
  const [freightDetail, setFreightDetail] = useState(null);
  const [loadingFreight, setLoadingFreight] = useState(false);
  const [previewState, setPreviewState] = useState(null);

  const selectedTemplate = useMemo(() => {
    const id = formData.manifest_template_id ? Number(formData.manifest_template_id) : null;
    if (!id) return null;
    return templates.find((t) => Number(t.id) === id) || null;
  }, [formData.manifest_template_id, templates]);

  const printData = useMemo(() => {
    if (!freightDetail || editingManifest) return null;
    return buildManifestDataFromFreight(freightDetail, formData);
  }, [freightDetail, formData, editingManifest]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [manifestsData, consigneesData, freightData, templatesData, depotsData] = await Promise.all([
        manifestsAPI.getAll(),
        consigneesAPI.getAll(),
        freightAPI.getAll(),
        manifestTemplatesAPI.getAll().catch(() => []),
        routingAPI.listDepots().catch(() => []),
      ]);
      setManifests(manifestsData);
      setConsignees(consigneesData);
      setFreightList(Array.isArray(freightData) ? freightData : []);
      setTemplates((Array.isArray(templatesData) ? templatesData : []).filter((t) => t.is_active !== false));
      const depots = Array.isArray(depotsData) ? depotsData : [];
      setDefaultDepot(depots.find((d) => d.is_default) || depots[0] || null);
    } catch (error) {
      alert('Error loading data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const applyFreightToForm = useCallback((freight) => {
    if (!freight) return;
    const shipDate = freight.pickup_date ? String(freight.pickup_date).slice(0, 10) : '';
    setFormData((prev) => ({
      ...prev,
      consignee_id: String(freight.consignee_id || ''),
      ship_date: shipDate || prev.ship_date,
      pieces: freight.quantity != null ? String(freight.quantity) : '',
      shipped_value: freight.value != null ? String(freight.value) : '',
      delivery_address: freight.consignee_address || '',
      delivery_city: freight.consignee_city || '',
      delivery_state: freight.consignee_state || '',
      delivery_zip_code: freight.consignee_zip_code || '',
    }));
  }, []);

  const loadFreightDetail = useCallback(async (freightId) => {
    if (!freightId) {
      setFreightDetail(null);
      return;
    }
    try {
      setLoadingFreight(true);
      const detail = await freightAPI.getById(freightId);
      setFreightDetail(detail);
      applyFreightToForm(detail);
    } catch (e) {
      setFreightDetail(null);
      alert('Error loading freight: ' + e.message);
    } finally {
      setLoadingFreight(false);
    }
  }, [applyFreightToForm]);

  useEffect(() => {
    if (!showModal || editingManifest) return;
    if (!formData.freight_id) {
      setFreightDetail(null);
      return;
    }
    loadFreightDetail(formData.freight_id);
  }, [showModal, editingManifest, formData.freight_id, loadFreightDetail]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!editingManifest) {
        if (!formData.freight_id) throw new Error('Select a freight record.');
        if (!formData.manifest_template_id) throw new Error('Select a manifest template.');
      }

      const submitData = {
        ...formData,
        manifest_number: formData.manifest_number.trim() || undefined,
        ship_date: formData.ship_date,
        service_level: formData.service_level || null,
        stop_number: formData.stop_number ? parseInt(formData.stop_number, 10) : null,
        pieces: formData.pieces ? parseInt(formData.pieces, 10) : null,
        shipped_value: formData.shipped_value ? parseFloat(formData.shipped_value) : null,
        consignee_id: parseInt(formData.consignee_id, 10),
        freight_id: formData.freight_id ? parseInt(formData.freight_id, 10) : null,
        manifest_template_id: formData.manifest_template_id
          ? parseInt(formData.manifest_template_id, 10)
          : null,
        delivery_address: formData.delivery_address || null,
        delivery_city: formData.delivery_city || null,
        delivery_state: formData.delivery_state || null,
        delivery_zip_code: formData.delivery_zip_code || null,
        delivery_window: formData.delivery_window || null,
        appointment_time: formData.appointment_time || null,
      };

      if (editingManifest) {
        await manifestsAPI.update(editingManifest.id, submitData);
      } else {
        await manifestsAPI.create(submitData);
      }
      setShowModal(false);
      setEditingManifest(null);
      resetForm();
      loadData();
    } catch (error) {
      alert('Error saving manifest: ' + error.message);
    }
  };

  const handleEdit = (item) => {
    setEditingManifest(item);
    setFreightDetail(null);
    setFormData({
      freight_id: item.freight_id ? String(item.freight_id) : '',
      manifest_template_id: item.manifest_template_id ? String(item.manifest_template_id) : '',
      manifest_number: item.manifest_number || '',
      ship_date: item.ship_date ? String(item.ship_date).slice(0, 10) : '',
      service_level: item.service_level || '',
      stop_number: item.stop_number ?? '',
      pieces: item.pieces ?? '',
      shipped_value: item.shipped_value ?? '',
      consignee_id: item.consignee_id || '',
      delivery_address: item.delivery_address || '',
      delivery_city: item.delivery_city || '',
      delivery_state: item.delivery_state || '',
      delivery_zip_code: item.delivery_zip_code || '',
      delivery_window: item.delivery_window || '',
      appointment_time: item.appointment_time ? item.appointment_time.slice(0, 16) : '',
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this manifest?')) return;
    try {
      await manifestsAPI.delete(id);
      loadData();
    } catch (error) {
      alert('Error deleting manifest: ' + error.message);
    }
  };

  const resetForm = () => {
    setFormData(emptyForm());
    setFreightDetail(null);
  };

  const deliveryFieldsFromConsignee = (consignee) => ({
    delivery_address: consignee?.address || '',
    delivery_city: consignee?.city || '',
    delivery_state: consignee?.state || '',
    delivery_zip_code: consignee?.zip_code || '',
  });

  const handleConsigneeChange = (consigneeId) => {
    const consignee = consignees.find((c) => String(c.id) === String(consigneeId));
    setFormData((prev) => ({
      ...prev,
      consignee_id: consigneeId,
      ...deliveryFieldsFromConsignee(consignee),
    }));
  };

  const openFormPreview = () => {
    if (!selectedTemplate?.fields?.length) {
      alert('Select a manifest template with at least one field.');
      return;
    }
    if (!printData) {
      alert('Select freight to build the manifest preview.');
      return;
    }
    const data = { ...printData, manifest_number: formData.manifest_number || printData.manifest_number };
    setPreviewState({
      name: selectedTemplate.name,
      fields: selectedTemplate.fields,
      data,
    });
  };

  const openRowPreview = async (row) => {
    if (!row.manifest_template_id || !row.freight_id) {
      alert('This manifest has no linked template or freight for printing.');
      return;
    }
    try {
      const [tplList, freight] = await Promise.all([
        manifestTemplatesAPI.getAll(),
        freightAPI.getById(row.freight_id),
      ]);
      const tpl = (tplList || []).find((t) => Number(t.id) === Number(row.manifest_template_id));
      if (!tpl?.fields?.length) {
        alert('Template not found or has no fields.');
        return;
      }
      const data = buildManifestDataFromFreight(freight, {
        manifest_number: row.manifest_number,
        ship_date: row.ship_date,
        service_level: row.service_level,
        stop_number: row.stop_number,
        pieces: row.pieces,
        shipped_value: row.shipped_value,
        delivery_address: row.delivery_address,
        delivery_city: row.delivery_city,
        delivery_state: row.delivery_state,
        delivery_zip_code: row.delivery_zip_code,
        delivery_window: row.delivery_window,
        appointment_time: row.appointment_time,
      });
      setPreviewState({ name: tpl.name, fields: tpl.fields, data });
    } catch (e) {
      alert('Error loading manifest for print: ' + e.message);
    }
  };

  const renderHammerAction = (row) => {
    const hasDelivery =
      row.delivery_address ||
      row.delivery_city ||
      (row.latitude != null && row.longitude != null) ||
      (row.delivery_latitude != null && row.delivery_longitude != null);
    if (!defaultDepot || !hasDelivery) return null;
    const url = buildHammerManifestUrl(defaultDepot, row);
    if (!url) return null;
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="btn-hammer btn-hammer-inline"
        title="Open depot → this delivery in Hammer (mobile GPS)"
      >
        Hammer
      </a>
    );
  };

  const renderActions = (row) => (
    <>
      {row.manifest_template_id && row.freight_id ? (
        <button
          type="button"
          className="btn-secondary"
          style={{ marginRight: 6 }}
          onClick={() => openRowPreview(row)}
        >
          Print
        </button>
      ) : null}
      {renderHammerAction(row)}
    </>
  );

  const columns = [
    { key: 'manifest_number', label: 'Manifest #' },
    { key: 'freight_number', label: 'Freight #' },
    { key: 'manifest_template_name', label: 'Template' },
    { key: 'consignee_name', label: 'Consignee' },
    { key: 'ship_date', label: 'Ship Date' },
    { key: 'service_level', label: 'Service' },
    { key: 'stop_number', label: 'Stop' },
    { key: 'pieces', label: 'Pieces' },
    {
      key: 'shipped_value',
      label: 'Value',
      format: (val) => (val != null ? `$${parseFloat(val).toFixed(2)}` : '-'),
    },
    { key: 'delivery_city', label: 'City' },
    { key: 'delivery_state', label: 'State' },
  ];

  const isAdd = !editingManifest;

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Manifest Management</h1>
        <button
          type="button"
          className="btn-primary"
          onClick={() => {
            setEditingManifest(null);
            resetForm();
            setShowModal(true);
          }}
        >
          Add New Manifest
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading...</div>
      ) : (
        <Table
          data={manifests}
          columns={columns}
          onEdit={handleEdit}
          onDelete={handleDelete}
          renderActions={renderActions}
        />
      )}

      {previewState ? (
        <ManifestPrintPreview
          templateName={previewState.name}
          fields={previewState.fields}
          data={previewState.data}
          onClose={() => setPreviewState(null)}
        />
      ) : null}

      <Modal
        show={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingManifest(null);
          resetForm();
        }}
        title={editingManifest ? 'Edit Manifest' : 'Add New Manifest'}
        size={isAdd ? 'xl' : 'md'}
      >
        <form onSubmit={handleSubmit} className="manifest-form">
          {isAdd ? (
            <>
              <div className="form-row">
                <div className="form-group">
                  <label>Manifest Template *</label>
                  <select
                    value={formData.manifest_template_id}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, manifest_template_id: e.target.value }))
                    }
                    required
                  >
                    <option value="">Select template</option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Freight *</label>
                  <select
                    value={formData.freight_id}
                    onChange={(e) => setFormData((p) => ({ ...p, freight_id: e.target.value }))}
                    required
                  >
                    <option value="">Select freight</option>
                    {freightList.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.freight_number}
                        {f.consignee_name ? ` — ${f.consignee_name}` : ''}
                        {f.status ? ` (${f.status})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {loadingFreight ? (
                <p style={{ color: '#666', fontSize: 13 }}>Loading freight details…</p>
              ) : null}
              {freightDetail ? (
                <p style={{ margin: '0 0 12px', fontSize: 13, color: '#555' }}>
                  Shipper: <strong>{freightDetail.shipper_name || '—'}</strong>
                  {' · '}
                  Carrier: <strong>{freightDetail.carrier_name || '—'}</strong>
                  {' · '}
                  Inventory lines: <strong>{(freightDetail.inventory_items || []).length}</strong>
                </p>
              ) : null}
            </>
          ) : null}

          {editingManifest ? (
            <div className="form-group">
              <label>Manifest Number *</label>
              <input
                type="text"
                value={formData.manifest_number}
                onChange={(e) => setFormData({ ...formData, manifest_number: e.target.value })}
                required
              />
            </div>
          ) : (
            <div className="form-group">
              <label>Manifest Number (leave blank to auto-generate)</label>
              <input
                type="text"
                placeholder="Auto-generated if blank"
                value={formData.manifest_number}
                onChange={(e) => setFormData({ ...formData, manifest_number: e.target.value })}
              />
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label>Ship Date *</label>
              <input
                type="date"
                value={formData.ship_date}
                onChange={(e) => setFormData({ ...formData, ship_date: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Service Level</label>
              <input
                type="text"
                placeholder="e.g. Standard, Express"
                value={formData.service_level}
                onChange={(e) => setFormData({ ...formData, service_level: e.target.value })}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Stop Number</label>
              <input
                type="number"
                value={formData.stop_number}
                onChange={(e) => setFormData({ ...formData, stop_number: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Pieces</label>
              <input
                type="number"
                value={formData.pieces}
                onChange={(e) => setFormData({ ...formData, pieces: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Shipped Value ($)</label>
              <input
                type="number"
                step="0.01"
                value={formData.shipped_value}
                onChange={(e) => setFormData({ ...formData, shipped_value: e.target.value })}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Consignee *</label>
            <select
              value={formData.consignee_id}
              onChange={(e) => handleConsigneeChange(e.target.value)}
              required
            >
              <option value="">Select Consignee</option>
              {consignees.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.company_name}
                </option>
              ))}
            </select>
          </div>

          <p style={{ margin: '0 0 0.75rem', fontSize: '0.9rem', color: '#555' }}>
            Delivery fields fill from the selected freight/consignee; you can edit before saving.
          </p>

          <div className="form-group">
            <label>Delivery Address</label>
            <textarea
              value={formData.delivery_address}
              onChange={(e) => setFormData({ ...formData, delivery_address: e.target.value })}
              rows="2"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Delivery City</label>
              <input
                type="text"
                value={formData.delivery_city}
                onChange={(e) => setFormData({ ...formData, delivery_city: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Delivery State</label>
              <input
                type="text"
                value={formData.delivery_state}
                onChange={(e) => setFormData({ ...formData, delivery_state: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Delivery Zip Code</label>
              <input
                type="text"
                value={formData.delivery_zip_code}
                onChange={(e) => setFormData({ ...formData, delivery_zip_code: e.target.value })}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Delivery Window</label>
              <input
                type="text"
                placeholder="e.g. 9AM-12PM"
                value={formData.delivery_window}
                onChange={(e) => setFormData({ ...formData, delivery_window: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Appointment Time</label>
              <input
                type="datetime-local"
                value={formData.appointment_time}
                onChange={(e) => setFormData({ ...formData, appointment_time: e.target.value })}
              />
            </div>
          </div>

          {isAdd ? (
            <div style={{ marginBottom: 12 }}>
              <button
                type="button"
                className="btn-secondary"
                onClick={openFormPreview}
                disabled={!formData.freight_id || !formData.manifest_template_id || loadingFreight}
              >
                Preview printable manifest
              </button>
            </div>
          ) : null}

          <div className="form-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                setShowModal(false);
                setEditingManifest(null);
                resetForm();
              }}
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              {isAdd ? 'Create manifest' : 'Save'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default ManifestManagement;
